'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { ChevronDownIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { AssistantWorkingMark } from './AssistantWorkingMark';
import { visibleAssistantToolArguments } from './activityPresentation';

const record = (value: unknown): Record<string, unknown> => value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
const labels: Record<string, string> = {
  get_student_context: 'Read academic context', search_course_sections: 'Search course sections',
  search_web: 'Search the web', preview_webpage: 'Read webpage', search_skills: 'Find relevant skills',
  read_skill: 'Read assistant guidance', search_tools: 'Find available tools',
  generate_schedule_options: 'Build schedule options', save_schedule: 'Save schedule',
  search_degrees: 'Find degree requirements', check_degree_progress: 'Check degree progress',
  get_selected_degree_plan: 'Read degree plan', present_ui: 'Prepare interactive answer',
  create_document_artifact: 'Prepare document', create_spreadsheet_artifact: 'Prepare spreadsheet',
};
const safeUrl = (value: unknown) => {
  try { const url = new URL(String(value)); return ['https:', 'http:'].includes(url.protocol) ? url : null; }
  catch { return null; }
};

export function activityCalls(parts: readonly unknown[], running: boolean) {
  return parts.flatMap((value, index) => {
    const part = record(value);
    const approval = record(part.approval);
    if (part.type !== 'tool-call') return [];
    const result = record(part.result);
    const data = record(result.data);
    const name = String(part.toolName ?? 'tool');
    const effectiveName = name === 'execute_capability' ? String(record(part.args).capabilityId ?? name) : name;
    const sources = ['search_web', 'preview_webpage'].includes(effectiveName)
      ? [...(Array.isArray(data.results) ? data.results : []), ...(data.url ? [data] : [])]
        .flatMap((source) => { const url = safeUrl(record(source).url); return url ? [{ url: url.href, label: url.hostname.replace(/^www\./, '') }] : []; })
      : [];
    const status = approval.approved === false ? 'Declined'
      : part.isError || result.success === false ? 'Failed'
      : part.approval && approval.approved === undefined ? 'Approval needed'
      : part.result !== undefined ? 'Done' : running ? 'Running' : 'Stopped';
    return [{ key: String(part.toolCallId ?? index), name: effectiveName,
      label: labels[effectiveName] ?? effectiveName.replaceAll('_', ' '), status,
      inputs: visibleAssistantToolArguments(name, part.args), sources }];
  });
}

/** Public action receipts only; provider reasoning parts are deliberately not rendered. */
export function AssistantActivity({ parts, running = false, interrupted = false, startedAt }: {
  parts: readonly unknown[]; running?: boolean; interrupted?: boolean; startedAt?: number | null;
}) {
  const [open, setOpen] = useState(false);
  const [elapsed, setElapsed] = useState<number | null>(null);
  const started = useRef<number | null>(null);
  const id = useId();
  useEffect(() => {
    if (!running) {
      if (started.current !== null) setElapsed(Math.max(1, Math.round((Date.now() - started.current) / 1000)));
      started.current = null;
      return;
    }
    started.current = startedAt ?? started.current ?? Date.now();
    setElapsed(Math.max(0, Math.floor((Date.now() - started.current) / 1000)));
    const timer = window.setInterval(() => setElapsed(Math.floor((Date.now() - started.current!) / 1000)), 1000);
    return () => window.clearInterval(timer);
  }, [running, startedAt]);
  const calls = activityCalls(parts, running);
  if (!running && !calls.length && elapsed === null) return null;
  const summary = calls.length ? `${calls.length} action${calls.length === 1 ? '' : 's'}` : '';
  const label = running ? `Working${elapsed ? ` for ${elapsed}s` : '…'}`
    : interrupted ? elapsed !== null ? `Stopped after ${elapsed}s · View activity` : 'Response stopped · View activity'
    : elapsed !== null ? `Worked for ${elapsed}s` : 'View activity';
  return (
    <div data-assistant-activity className="mb-3 min-w-0 text-sm text-[var(--ss-text-muted)]">
      <div className="flex items-center gap-2">
      {running ? <AssistantWorkingMark /> : null}
      <button type="button" aria-label={label} aria-expanded={open} aria-controls={id} onClick={() => setOpen(!open)}
        className="inline-flex min-h-12 max-w-full items-center gap-2 rounded-control text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] md:min-h-9">
        <span>{label}{summary ? <span className="ml-2 text-xs">· {summary}</span> : null}</span><ChevronDownIcon aria-hidden="true" className={`size-4 shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>
      {running ? <span role="status" className="sr-only">Scarlet Sync is working</span> : null}
      </div>
      {open ? <div id={id} className="mt-1 overflow-hidden rounded-card border border-[var(--ss-border)]">
        <p className="px-4 py-3 text-xs leading-5">{running ? 'Thinking and checking the information needed for your answer.' : 'Steps used to prepare this response.'}</p>
        {calls.length ? <ol aria-label="Response activity" className="divide-y divide-[var(--ss-border)]">
          {calls.map((call) => <li key={call.key}>
            <details className="group/call">
              <summary className="flex min-h-12 cursor-pointer list-none items-center gap-2 px-4 py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] md:min-h-9 [&::-webkit-details-marker]:hidden">
                {call.sources.length ? <GlobeAltIcon className="size-4 shrink-0" aria-hidden="true" /> : null}
                <span className="min-w-0 break-words text-[var(--ss-text)]">{call.label}</span>
                <span className="ml-auto shrink-0 text-xs">{call.status}</span>
                <ChevronDownIcon className="size-4 shrink-0 group-open/call:rotate-180" aria-hidden="true" />
              </summary>
              <div className="px-4 pb-3 text-xs leading-5">
                {call.inputs ? <dl className="divide-y divide-[var(--ss-border)]">{Object.entries(call.inputs).map(([key, value]) => <div key={key} className="min-w-0 break-words [overflow-wrap:anywhere]"><dt className="inline font-medium">{key}: </dt><dd className="inline">{typeof value === 'string' ? value : JSON.stringify(value)}</dd></div>)}</dl> : <p>Private details are hidden.</p>}
                {call.sources.length ? <div className="mt-2 flex flex-wrap gap-2">{call.sources.map((source, index) => <a key={`${source.url}-${index}`} href={source.url} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-12 max-w-full items-center rounded-control bg-[var(--ss-surface-subtle)] px-3 text-[var(--ss-link)] underline focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] md:min-h-9"><span className="break-all">{source.label}</span></a>)}</div> : null}
              </div>
            </details>
          </li>)}
        </ol> : <p className="px-4 pb-3 text-xs">{running ? 'Preparing a response…' : 'No tool calls were used.'}</p>}
      </div> : null}
    </div>
  );
}
